'use client';

import type React from 'react';
import { useEffect } from 'react';

import type { DetailTab } from '@/types/events';
import { explorerAnalytics } from '@/lib/analytics';
import { useExplorer } from '@/hooks/useExplorer';
import { retrieveEvent } from '@/services/events/EventsService';
import { useEventExplorerStore } from '@/stores/useEventExplorerStore';
import { Button } from '@components/components/ui/button';
import { cn } from '@components/lib/utils';
import { EventDetailHeader } from './EventDetailHeader';
import { EventEmptyState } from './EventEmptyState';
import { EventForwardTab } from './EventForwardTab';
import { EventJsonViewer } from './EventJsonViewer';
import { EventWebhookAttempts } from './EventWebhookAttempts';

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'json', label: 'Event Payload' },
  { id: 'webhooks', label: 'Webhook Attempts' },
  { id: 'forward', label: 'Send to Server' },
];

export const EventDetailPanel: React.FC = () => {
  const { siteName, apiKey } = useExplorer();
  const {
    selectedEventId,
    selectedEventDetail,
    isLoadingDetail,
    activeDetailTab,
    setSelectedEventDetail,
    setIsLoadingDetail,
    setActiveDetailTab,
  } = useEventExplorerStore();

  // Fetch full event detail on selection
  useEffect(() => {
    if (!selectedEventId || !siteName || !apiKey) {
      setIsLoadingDetail(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchDetail = async () => {
      setIsLoadingDetail(true);
      try {
        const response = await retrieveEvent(
          siteName,
          apiKey,
          selectedEventId,
          controller.signal
        );
        if (!cancelled) {
          setSelectedEventDetail(response.event);
        }
      } catch (err) {
        if (!cancelled && (err as Error).name !== 'AbortError') {
          setSelectedEventDetail(null);
          setIsLoadingDetail(false);
        }
      }
    };

    fetchDetail();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    selectedEventId,
    siteName,
    apiKey,
    setSelectedEventDetail,
    setIsLoadingDetail,
  ]);

  if (isLoadingDetail) {
    return (
      <EventEmptyState type="loading" message="Loading event details..." />
    );
  }

  if (!selectedEventId) {
    return <EventEmptyState type="no-selection" />;
  }

  if (!selectedEventDetail) {
    return (
      <EventEmptyState type="error" message="Failed to load event details" />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <EventDetailHeader event={selectedEventDetail} />

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 px-4">
        {TABS.map((tab) => (
          <Button
            key={tab.id}
            variant="ghost"
            size="sm"
            onClick={() => {
              explorerAnalytics.eventDetailTabSwitched({
                tabId: tab.id,
                eventType: selectedEventDetail?.event_type,
              });
              setActiveDetailTab(tab.id);
            }}
            className={cn(
              'px-4 py-2.5 relative rounded-none',
              activeDetailTab === tab.id
                ? 'text-shade-teal-700'
                : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {tab.label}
            {activeDetailTab === tab.id && (
              <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-shade-teal-500" />
            )}
          </Button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-auto">
        {activeDetailTab === 'webhooks' ? (
          <EventWebhookAttempts webhooks={selectedEventDetail.webhooks} />
        ) : activeDetailTab === 'forward' ? (
          <EventForwardTab event={selectedEventDetail} />
        ) : (
          <EventJsonViewer content={selectedEventDetail} />
        )}
      </div>
    </div>
  );
};
